import { CaptureTreeDragCursor } from "@/components/ui/tree/capture-tree/capture-tree-drag-cursor";
import { TreeHeightWrapper } from "@/components/ui/tree/tree-height-wrapper";
import { TREE_NODE_HEIGHT } from "@/components/ui/tree/tree-node";
import { useEditConnectionContext } from "@/data-preparation-tool/edit-connection-context";
import { loadRevisionDataThunk } from "@/data-preparation-tool/loading/load-revision-data-thunk";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { useToast } from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/foundation";
import {
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
  isSiteScapeClusterEntity,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useForkRef } from "@mui/material";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type Ref,
} from "react";
import {
  NodeApi,
  NodeRendererProps,
  RowRendererProps,
  Tree,
  TreeApi,
} from "react-arborist";
import { OpenMap } from "react-arborist/dist/module/state/open-slice";
import { useDragDropManager, useDrop } from "react-dnd";
import {
  selectIsSavingHierarchyChanges,
  selectSelectedEntityIds,
} from "../../store/data-preparation-ui/data-preparation-ui-selectors";
import {
  setDidHierarchySaveFail,
  setIsSavingHierarchyChanges,
  setSelectedEntityIds,
} from "../../store/data-preparation-ui/data-preparation-ui-slice";
import {
  selectRegistrationRevision,
  selectRevisionEntities,
  selectRevisionEntityMap,
} from "../../store/revision/revision-entity-selectors";
import { moveEntities, setEntities } from "../../store/revision/revision-slice";
import { selectDataPreparationTransformCache } from "../../store/user-edits/data-preparation-user-edit-selectors";
import {
  areAllDragSourcesValid,
  calculateNewPosesForMove,
  DropPosition,
  excludeDescendantsOfDraggedEntities,
  getDropPosition,
  isValidDropTarget,
  saveMovedEntities,
  sortByTreeOrder,
} from "./scan-tree-drag-drop-utils";
import { ScanTreeNode } from "./scan-tree-node";
import { ScanTreeNodeHoverProvider } from "./scan-tree-node-hover-context";
import { ScanTreeRow } from "./scan-tree-row";

type ScanTreeProps = {
  /** The entities to show in the tree. */
  entities: CaptureTreeEntityRevision[];
  /** Ref for controlling the tree. */
  ref?: Ref<TreeApi<CaptureTreeEntityRevision>>;
  /** Called when a node is expanded or collapsed. */
  onToggle?(): void;
};

/** Based on the interface of react-arborist's Tree component. */
type DisableDropParams = {
  /** The node that is the target of the drop. */
  parentNode: NodeApi<CaptureTreeEntityRevision>;
  /** The nodes that are being dragged. */
  dragNodes: Array<NodeApi<CaptureTreeEntityRevision>>;
};

/** Based on the interface of react-arborist's Tree component. */
type OnMoveParams = {
  /** The node that is the target of the drop. */
  parentNode: NodeApi<CaptureTreeEntityRevision> | null;
  /** The nodes that are being dragged. */
  dragNodes: Array<NodeApi<CaptureTreeEntityRevision>>;
};

/** @returns A tree of scans in clusters. */
export function ScanTree({
  entities,
  ref,
  onToggle,
}: ScanTreeProps): JSX.Element {
  const { projectApiClient } = useApiClientContext();
  const dragDropManager = useDragDropManager();
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const { openToast } = useToast();
  const { handleErrorWithToast } = useErrorHandlers();
  const { handleAddConnection } = useEditConnectionContext();
  const entityMap = useAppSelector(selectRevisionEntityMap);
  const isSimpleScanOrganization = useAppSelector(
    selectHasFeature(Features.SimpleScanOrganization),
  );
  const selectedEntityIds = useAppSelector(selectSelectedEntityIds);
  const registrationRevision = useAppSelector(selectRegistrationRevision);
  const isSaving = useAppSelector(selectIsSavingHierarchyChanges);
  const [hierarchySaveTargetId, setHierarchySaveTargetId] = useState<GUID>();

  // Map for a quick children lookup by id
  const entityChildrenMap = useEntityChildrenMap(entities);

  // The children of the root entity
  const rootEntities = useMemo(() => {
    const rootEntityId = entities.find(
      (e) => e.type === CaptureTreeEntityType.root,
    )?.id;

    if (!rootEntityId) return [];

    return entityChildrenMap.get(rootEntityId);
  }, [entities, entityChildrenMap]);

  const initialOpenState = useMemo(() => {
    const openMap: OpenMap = {};

    for (const entity of entities) {
      if (entity.type === CaptureTreeEntityType.cluster) {
        // All clusters are collapsed by default on initial render
        openMap[entity.id] = false;
      }
    }

    return openMap;
  }, [entities]);

  const treeRef = useRef<TreeApi<CaptureTreeEntityRevision>>(undefined);
  const forkedRef = useForkRef(ref, treeRef);
  // Stores the full intended drag selection computed in onDragStart so that onMove can use it.
  // This is necessary because the selection state updates in onDragStart must be deferred
  // (to avoid aborting the drag), but onMove needs the complete selection. Without this, if the
  // user starts dragging a non-selected node, onMove would only move the non-selected node, not
  // the other selected entities.
  const draggedEntitiesRef = useRef<CaptureTreeEntityRevision[] | null>(null);

  // True while the selection-sync effect is programmatically calling `tree.setSelection`, so the
  // tree's own `onSelect` callback (fired synchronously by `setSelection`) can tell apart a
  // genuine user interaction with the tree from our own Redux -> tree sync. This is necessary
  // because right after expanding, newly opened node isn't in the tree's visible
  // node list yet (that requires a re-render), so `tree.selectedNodes` used internally by
  // `setSelection` would omit it and `onSelect` would otherwise dispatch an incomplete selection,
  // clobbering the correct one that triggered this sync in the first place.
  const isSyncingSelectionRef = useRef(false);

  // True once a drop of the current drag operation was handled, so that a drop on a row isn't
  // handled a second time by the outer drop target. react-dnd's monitor.didDrop() can't be used
  // instead, as it is also true after react-arborist's own outer drop target, which ignores drops.
  const isDropHandledRef = useRef(false);

  const handleExpandNode = useCallback((entityId: GUID) => {
    treeRef.current?.open(entityId);
  }, []);

  /**
   * @returns The position among the children of the drop target at which the dragged entities
   *  are currently dropped, or undefined if they are dropped onto the drop target itself.
   */
  const getCurrentDropPosition = useCallback(
    (
      parentNode: NodeApi<CaptureTreeEntityRevision> | null,
      draggedIds: GUID[],
    ): DropPosition | undefined => {
      const tree = treeRef.current;
      // react-arborist passes index 0 to its callbacks when dropping onto a node, so its state
      // is needed to tell that apart from dropping at the first position.
      const index = tree?.state.dnd.index;
      if (!tree || index === null || index === undefined) return;

      const siblingIds =
        (parentNode ?? tree.root).children?.map((node) => node.id) ?? [];
      return getDropPosition(siblingIds, draggedIds, index);
    },
    [],
  );

  // Sync the selection state with the tree
  useEffect(() => {
    const tree = treeRef.current;
    if (!tree) return;

    // Prevent infinite loop due to state sync
    const treeSelectedIds = Array.from(tree.selectedIds);
    if (
      selectedEntityIds.length === treeSelectedIds.length &&
      selectedEntityIds.every((id, index) => id === treeSelectedIds[index])
    ) {
      return;
    }

    isSyncingSelectionRef.current = true;
    tree.setSelection({
      ids: selectedEntityIds,
      anchor: selectedEntityIds.at(0) ?? null,
      mostRecent: selectedEntityIds.at(-1) ?? null,
    });
    isSyncingSelectionRef.current = false;
    tree.focus(selectedEntityIds.at(-1) ?? null);
  }, [getState, selectedEntityIds]);

  const childrenAccessor = useCallback(
    (entity: CaptureTreeEntityRevision) => {
      if (!showChildren(entity)) return null;
      // If the entity has no children, return an empty array instead of null since null would
      // indicate that the entity can't have children at all and could therefore not serve as a
      // drop target.
      return entityChildrenMap.get(entity.id) ?? [];
    },
    [entityChildrenMap],
  );

  /** Disables the drag functionality for entities that are not valid drag sources. */
  const disableDrag = useCallback(
    (dragSource: CaptureTreeEntityRevision): boolean => {
      if (!isSimpleScanOrganization || isSaving) {
        return true;
      }

      // See the comment for onDragStart.
      const isDragSourceSelected =
        treeRef.current?.selectedIds.has(dragSource.id) ?? false;
      const draggedEntities = isDragSourceSelected
        ? (treeRef.current?.selectedNodes.map((node) => node.data) ?? [
            dragSource,
          ])
        : [dragSource];

      return !areAllDragSourcesValid(draggedEntities, entityMap);
    },
    [isSimpleScanOrganization, isSaving, entityMap],
  );

  /** Disables the drop functionality for entities that are not valid drop targets. */
  const disableDrop = useCallback(
    ({ parentNode, dragNodes }: DisableDropParams): boolean => {
      if (!isSimpleScanOrganization || isSaving) {
        return true;
      }

      // Validate the entities that are actually moved, see onMove. dragNodes lacks selected
      // entities in collapsed clusters, as react-arborist only includes visible nodes.
      const draggedEntities =
        draggedEntitiesRef.current ?? dragNodes.map((node) => node.data);
      const draggedIds = excludeDescendantsOfDraggedEntities(
        draggedEntities,
        entityMap,
      ).map((entity) => entity.id);
      return !isValidDropTarget(
        parentNode,
        draggedIds,
        entityMap,
        getCurrentDropPosition(parentNode, draggedIds),
      );
    },
    [isSimpleScanOrganization, isSaving, entityMap, getCurrentDropPosition],
  );

  /**
   * Handles the drag start on a tree node.
   * Matches the behavior of the Windows Explorer.
   *  - If the dragged entity is selected, all selected entities are dragged.
   *  - If the dragged entity is not selected, only that entity is dragged and the selection is
   *    updated to include only that entity.
   *  - If any dragged entity isn't a valid drag source, a warning toast is shown.
   */
  const onDragStart = useCallback(
    (attemptedNode: NodeApi<CaptureTreeEntityRevision>) => {
      isDropHandledRef.current = false;
      if (!isSimpleScanOrganization || isSaving) {
        return;
      }

      const tree = treeRef.current;
      const selectedIds = tree?.selectedIds ? Array.from(tree.selectedIds) : [];

      const isDraggedEntitySelected =
        tree?.selectedIds.has(attemptedNode.id) ?? false;
      const draggedIds = isDraggedEntitySelected
        ? selectedIds
        : [attemptedNode.id];

      const draggedEntities = draggedIds
        .map((id) => entityMap[id])
        .filter((entity): entity is CaptureTreeEntityRevision => !!entity);

      // Store the intended drag selection synchronously so onMove can use it even though
      // the visible selection update below is deferred.
      draggedEntitiesRef.current = draggedEntities;

      const hasForbiddenDragSource = !areAllDragSourcesValid(
        draggedEntities,
        entityMap,
      );
      if (hasForbiddenDragSource) {
        openToast({
          title: "Captures can't be moved",
          message:
            "Some of the selected captures can't be moved, for example individual captures inside an Orbis cluster.",
          variant: "warning",
        });
      }

      if (!isDraggedEntitySelected) {
        // Defer state mutations that trigger re-renders until after the dragstart event completes.
        // Synchronous DOM mutations during dragstart cause all browsers to abort the drag operation.
        setTimeout(() => {
          if (tree) {
            tree.setSelection({
              ids: draggedIds,
              anchor: draggedIds.at(0) ?? null,
              mostRecent: attemptedNode.id,
            });
          }
          dispatch(setSelectedEntityIds(draggedIds));
        }, 0);
      }
    },
    [dispatch, entityMap, isSimpleScanOrganization, isSaving, openToast],
  );

  /**
   * Handles moving entities in the tree at the end of a drag and drop operation.
   * It validates (again) that the move is valid and then calculates the new local poses for the moved entities.
   */
  const onMove = useCallback(
    async ({ parentNode, dragNodes }: OnMoveParams): Promise<void> => {
      isDropHandledRef.current = true;
      if (!isSimpleScanOrganization || isSaving) {
        return;
      }

      try {
        dispatch(setIsSavingHierarchyChanges(true));
        dispatch(setDidHierarchySaveFail(false));

        function showUnexpectedErrorToast(): void {
          openToast({
            title: "Unexpected error",
            message:
              "An unexpected error occurred. Please reload the page and try again.",
            variant: "error",
          });
        }

        if (!registrationRevision) {
          showUnexpectedErrorToast();
          return;
        }

        // Use the intended drag selection captured in onDragStart (which may include a node that
        // was auto-selected at drag start). Fall back to dragNodes if no pending selection is set.
        const draggedEntities =
          draggedEntitiesRef.current ?? dragNodes.map((node) => node.data);
        draggedEntitiesRef.current = null;

        // If a cluster and some or all of its descendants are both selected, exclude the descendants.
        // They will move implicitly when the cluster is moved.
        // The remaining entities keep their order from the tree, independent of the selection order.
        const filteredDraggedEntities = sortByTreeOrder(
          excludeDescendantsOfDraggedEntities(draggedEntities, entityMap),
          rootEntities ?? [],
          entityChildrenMap,
        );
        const draggedIds = filteredDraggedEntities.map((entity) => entity.id);
        // Must be read before any await, while the tree's drag and drop state is still set.
        const dropPosition = getCurrentDropPosition(parentNode, draggedIds);

        // This was already validated in disableDrag and disableDrop, but it's done here again to be safe.
        const isValidDrag = areAllDragSourcesValid(
          filteredDraggedEntities,
          entityMap,
        );
        if (!isValidDrag) {
          showUnexpectedErrorToast();
          return;
        }
        // If parentNode is null, the user dropped into the menu's root node, which doesn't
        // correspond to any entity. In this case, the dragged entities will be reparented to the
        // root entity.
        const isValidDrop = isValidDropTarget(
          parentNode,
          draggedIds,
          entityMap,
          dropPosition,
        );
        if (!isValidDrop) {
          showUnexpectedErrorToast();
          return;
        }
        const dropTargetId =
          parentNode?.data.id ??
          Object.values(entityMap).find(
            (e) => e?.type === CaptureTreeEntityType.root,
          )?.id;
        if (!dropTargetId) {
          showUnexpectedErrorToast();
          return;
        }

        // Read the transform cache on-demand instead of subscribing to it, to avoid re-rendering
        // the tree on every frame while dragging a scan marker in the map view.
        const state = getState();
        const transformCache = selectDataPreparationTransformCache(state);

        const newPoses = calculateNewPosesForMove(
          dropTargetId,
          draggedIds,
          transformCache,
        );
        if (newPoses === null) {
          showUnexpectedErrorToast();
          return;
        }

        // Show the move in the tree and 3D view right away, before the backend confirmed it.
        const previousEntities = selectRevisionEntities(state);
        const beforeSiblingId = dropPosition?.beforeSiblingId;
        setHierarchySaveTargetId(dropTargetId);
        dispatch(
          moveEntities({
            entities: filteredDraggedEntities.map((entity) => ({
              ...entity,
              parentId: dropTargetId,
              pose: newPoses[entity.id] ?? entity.pose,
            })),
            beforeSiblingId,
          }),
        );

        // Persist the changes to the moved entities.
        try {
          await saveMovedEntities({
            projectApiClient,
            state,
            revision: registrationRevision,
            movedEntities: filteredDraggedEntities,
            newParentId: dropTargetId,
            // Without a next sibling, also when dropped onto the parent itself, null explicitly
            // places the entities last, like the update of the store above.
            beforeSiblingId: beforeSiblingId ?? null,
            newPoses,
          });
        } catch (error: unknown) {
          // Roll back the optimistic update
          dispatch(setEntities(previousEntities));
          dispatch(setDidHierarchySaveFail(true));
          handleErrorWithToast({
            error,
            title:
              "Failed to save moved items. Please reload the page and try again.",
          });
          return;
        }

        try {
          await dispatch(loadRevisionDataThunk({ projectApiClient }));
        } catch (error: unknown) {
          handleErrorWithToast({
            error,
            title: "Failed to load updated data. Please reload the page.",
          });
        }
      } finally {
        setHierarchySaveTargetId(undefined);
        dispatch(setIsSavingHierarchyChanges(false));
      }
    },
    [
      isSimpleScanOrganization,
      isSaving,
      registrationRevision,
      entityMap,
      openToast,
      dispatch,
      getState,
      projectApiClient,
      handleErrorWithToast,
      getCurrentDropPosition,
      rootEntities,
      entityChildrenMap,
    ],
  );

  // react-arborist shows a drop line in the empty area below the last row, but its drop target
  // there has no drop handler. Such drops are handled here, using the drop position that
  // react-arborist computed while hovering. Drops on rows are handled by react-arborist itself.
  const [{ isDragOverTree }, connectOuterDropTarget] = useDrop(
    () => ({
      accept: "NODE",
      // Follow react-arborist, so that the cursor doesn't suggest that invalid drops are possible
      canDrop: () => treeRef.current?.canDrop() ?? false,
      drop: () => {
        const tree = treeRef.current;
        if (isDropHandledRef.current || !tree) return;

        onMove({
          parentNode: tree.get(tree.state.dnd.parentId),
          dragNodes: tree.dragNodes,
        });
      },
      collect: (monitor) => ({ isDragOverTree: monitor.isOver() }),
    }),
    [onMove],
  );

  // react-arborist only updates its drop line while hovering the tree, so it would keep showing
  // the last drop position after leaving the tree, although nothing happens when dropping there.
  useEffect(() => {
    if (!isDragOverTree) {
      treeRef.current?.hideCursor();
    }
  }, [isDragOverTree]);

  const outerDropTargetRef = useCallback(
    (element: HTMLDivElement | null) => {
      connectOuterDropTarget(element);
    },
    [connectOuterDropTarget],
  );

  // The render callbacks need to be stable: react-arborist recreates all its node instances
  // whenever any of its props change, which re-renders every row of the tree.
  const renderRow = useCallback(
    (props: RowRendererProps<CaptureTreeEntityRevision>) => (
      <ScanTreeRow {...props} onDragStart={onDragStart} />
    ),
    [onDragStart],
  );

  const onSelect = useCallback(
    (nodes: Array<NodeApi<CaptureTreeEntityRevision>>) => {
      // Skip: this selection change was triggered by our own Redux -> tree sync effect, not
      // a genuine user interaction with the tree, so it must not be dispatched back. At this
      // point `nodes` can be missing entities whose parent clusters were just opened (they
      // aren't in the tree's visible node list yet), so dispatching would clobber the
      // correct selection with an incomplete one.
      if (isSyncingSelectionRef.current) {
        return;
      }

      const selectedIds = nodes.map((node) => node.data.id);
      const lastScanId = selectedIds.at(-1);
      if (handleAddConnection(lastScanId)) {
        return;
      }

      dispatch(setSelectedEntityIds(selectedIds));
    },
    [dispatch, handleAddConnection],
  );

  const nodeToRender = useCallback(
    (props: NodeRendererProps<CaptureTreeEntityRevision>) => (
      <ScanTreeNode
        {...props}
        showContextMenu
        hierarchySaveTargetId={hierarchySaveTargetId}
        isSavingHierarchyChanges={isSaving}
        onExpandNode={handleExpandNode}
      />
    ),
    [handleExpandNode, hierarchySaveTargetId, isSaving],
  );

  return (
    <ScanTreeNodeHoverProvider>
      <TreeHeightWrapper ref={outerDropTargetRef}>
        <Tree
          dndManager={dragDropManager}
          initialOpenState={initialOpenState}
          ref={forkedRef}
          data={rootEntities}
          width="100%"
          disableDrag={disableDrag}
          disableDrop={disableDrop}
          onMove={onMove}
          rowHeight={TREE_NODE_HEIGHT}
          renderCursor={CaptureTreeDragCursor}
          indent={24}
          renderRow={renderRow}
          onSelect={onSelect}
          onToggle={onToggle}
          childrenAccessor={childrenAccessor}
        >
          {nodeToRender}
        </Tree>
      </TreeHeightWrapper>
    </ScanTreeNodeHoverProvider>
  );
}

/**
 * @param entity The entity to determine whether to show the children of.
 * @returns true if the children of the entity should be shown in the scan tree; else false.
 */
function showChildren(entity: CaptureTreeEntityRevision): boolean {
  // SiteScape captures are viewed as one single unit by the user
  // Don't show the individual contained scans, which are more of an implementation detail
  return (
    (entity.type === CaptureTreeEntityType.root ||
      entity.type === CaptureTreeEntityType.cluster) &&
    !isSiteScapeClusterEntity(entity)
  );
}

/**
 * @returns a map for quick children lookup of entities
 * @param entities the entities to create the map for
 */
export function useEntityChildrenMap(
  entities: CaptureTreeEntityRevision[],
): Map<GUID, CaptureTreeEntityRevision[]> {
  return useMemo(() => {
    const map = new Map<GUID, CaptureTreeEntityRevision[]>();

    for (const entity of entities) {
      if (!entity.parentId) continue;

      let childrenOfParent = map.get(entity.parentId);

      if (!childrenOfParent) {
        childrenOfParent = [];
        map.set(entity.parentId, childrenOfParent);
      }

      childrenOfParent.push(entity);
    }

    return map;
  }, [entities]);
}
